import { format } from 'date-fns';
import { getEnvironmentSettings } from '@pb/services';

import {
    TDiaryNotification,
    TMessageNotification,
    TOffersNotifications,
    TViewingsNotifications,
    TDiaryNotifications,
    TMessagesNotifications,
    TFeedbackNotifications,
    TNotificationTypes,
    TSetCopy,
    TNotifications,
    TNotificationAreas,
    TNormalisedNotification,
    TBaseNotification,
} from './notifications.types';

function setNotificationCopyAndLink(
    notificationType: string,
    notificationId: number,
    additionalInfo?: string | Date
): TSetCopy {
    const { baseUrl, messages } = getEnvironmentSettings();

    let dateHumanPretty;
    let dateQueryParameter;

    if (notificationType === 'appointmentInDiary') {
        dateHumanPretty = format(additionalInfo as Date, 'do MMMM yyyy');
        dateQueryParameter = format(additionalInfo as Date, 'yyyy-MM-dd');
    }

    const copy: { [key: string]: TSetCopy } = {
        offerToReview: {
            copy: `You've received a new offer!`,
            url: `${baseUrl}/seller#!manageOffers?offerid=${notificationId}`,
        },
        updateToOffer: {
            copy: `You've received an updated offer!`,
            url: `${baseUrl}/seller#!manageOffers?offerid=${notificationId}`,
        },
        lawyerDetailsRequired: {
            copy: `Lawyer details required on an offer`,
            url: `${baseUrl}/seller#!manageOffers?offerid=${notificationId}`,
        },
        scotlandpayReferencingFee: {
            copy: `Reference fee payment required`,
            url: `${baseUrl}/seller#!manageOffers?offerid=${notificationId}`,
        },
        payReferencingFee: {
            copy: `Holding deposit payment required`,
            url: `${baseUrl}/seller#!manageOffers?offerid=${notificationId}`,
        },
        submitReferencing: {
            copy: `Reference submission required`,
            url: `${baseUrl}/seller#!manageOffers?offerid=${notificationId}`,
        },
        outcomeOfViewing: {
            copy: `Please confirm if your previous viewing was attended`,
            url: `${baseUrl}/seller#!manageViewings?viewingid=${notificationId}`,
        },
        appointmentInDiary: {
            copy: `Appointment confirmation required for ${dateHumanPretty}`,
            url: `${baseUrl}/seller#!manageDiary?date=${dateQueryParameter}`,
        },
        unreadMessage: {
            copy: `You have an unread message from ${additionalInfo}`,
            url: `${messages}`,
        },
        provideFeedback: {
            copy: `Feedback on your last viewing required`,
            url: `${baseUrl}/seller#!manageFeedback?feedbackid=${notificationId}`,
        },
        feedbackToReview: {
            copy: `You've received new feedback!`,
            url: `${baseUrl}/seller#!manageFeedback?feedbackid=${notificationId}`,
        },
    };

    return copy[notificationType];
}

function filterKeys(notification: string): boolean {
    return !['id', 'lastUpdated'].includes(notification);
}

function getAdditionalInformation(
    notification: TMessageNotification | TDiaryNotification
): string | Date {
    if ('from' in notification) {
        return notification.from;
    }
    if ('appointmentDateTime' in notification) {
        return notification.appointmentDateTime
            ? new Date(notification.appointmentDateTime)
            : new Date();
    }

    return '';
}

function normaliseNotifications<T extends TBaseNotification>(
    notifications: T,
    notificationArea: TNotificationAreas,
    id: string
) {
    return function normaliseNotificationsClosure(
        accumulator: TNormalisedNotification[],
        currentNotificationType: TNotificationTypes
    ) {
        if (
            !notifications ||
            // @ts-expect-error
            !notifications[currentNotificationType].length > 0
        ) {
            return accumulator;
        }

        return [
            ...accumulator,
            // @ts-ignore
            ...notifications[currentNotificationType].map(notification => ({
                area: notificationArea,
                type: currentNotificationType,
                lastUpdated: notifications!.lastUpdated,
                ...setNotificationCopyAndLink(
                    currentNotificationType === 'payReferencingFee' &&
                        notification.toLet
                        ? 'scotlandpayReferencingFee'
                        : currentNotificationType,
                    notification[id],
                    ['diary', 'messages'].includes(notificationArea)
                        ? getAdditionalInformation(notification)
                        : undefined
                ),
            })),
        ];
    };
}

function setNotificationContent<T extends TBaseNotification>(
    notifications: T,
    notificationArea: TNotificationAreas,
    notificationId: string
): TNormalisedNotification[] {
    const normaliseOfferNotification = normaliseNotifications<T>(
        notifications,
        notificationArea,
        notificationId
    );

    return (
        Object.keys(notifications).filter(filterKeys) as TNotificationTypes[]
    ).reduce(normaliseOfferNotification, [] as TNormalisedNotification[]);
}

export function normaliseNotificationData(
    notifications: TNotifications
): TNormalisedNotification[] {
    return (Object.keys(notifications) as TNotificationAreas[]).reduce(
        (accumulator, currentArea) => {
            if (!notifications[currentArea]) {
                return accumulator;
            }

            let normalisedNotification: TNormalisedNotification[] = [];

            if (currentArea === 'offers') {
                normalisedNotification = [
                    ...setNotificationContent<TOffersNotifications>(
                        notifications[currentArea],
                        currentArea,
                        'offerId'
                    ),
                ];
            }

            if (currentArea === 'viewings') {
                normalisedNotification = [
                    ...setNotificationContent<TViewingsNotifications>(
                        notifications[currentArea],
                        currentArea,
                        'viewingId'
                    ),
                ];
            }

            if (currentArea === 'diary') {
                normalisedNotification = [
                    ...setNotificationContent<TDiaryNotifications>(
                        notifications[currentArea],
                        currentArea,
                        'id'
                    ),
                ];
            }

            if (currentArea === 'messages') {
                normalisedNotification = [
                    ...setNotificationContent<TMessagesNotifications>(
                        notifications[currentArea],
                        currentArea,
                        'messageId'
                    ),
                ];
            }

            if (currentArea === 'feedback') {
                normalisedNotification = [
                    ...setNotificationContent<TFeedbackNotifications>(
                        notifications[currentArea],
                        currentArea,
                        'FeedbackId'
                    ),
                ];
            }

            return [...accumulator, ...normalisedNotification];
        },
        [] as TNormalisedNotification[]
    );
}
